import { Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter and on the magnifier. Omit for search-as-you-type. */
  onSubmit?: (value: string) => void;
  placeholder?: string;
  /** Defaults to the placeholder, so the field is always announced. */
  ariaLabel?: string;
  maxLength?: number;
  disabled?: boolean;
  /** `sm` matches a toolbar row; `md` matches a form control. */
  size?: "sm" | "md";
  /** Width and layout are the caller's business. */
  className?: string;
  id?: string;
  autoFocus?: boolean;
}

const SIZES = {
  sm: { field: "h-8 pl-8 pr-8 text-xs", icon: "left-2.5 size-3.5", clear: "right-2 size-3.5" },
  md: { field: "h-9 pl-9 pr-9 text-sm", icon: "left-3 size-4", clear: "right-2.5 size-4" },
} as const;

/**
 * The app's search field: magnifier, input, and a clear button that appears
 * once something is typed.
 *
 * Without `onSubmit` it is a filter-as-you-type box. With it, typing only
 * updates the field and Enter (or the magnifier) is what reaches the caller —
 * the shape a server-side search wants. Emptying the box, however it happens
 * (backspace, the clear button, Escape), always submits an empty term, so the
 * full list comes back without a further keystroke.
 */
export function SearchInput({
  value,
  onChange,
  onSubmit,
  placeholder = "Search",
  ariaLabel,
  maxLength = 80,
  disabled = false,
  size = "md",
  className = "",
  id,
  autoFocus = false,
}: SearchInputProps) {
  const sizing = SIZES[size];

  const clear = () => {
    onChange("");
    onSubmit?.("");
  };

  return (
    <div className={`relative ${className}`}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        tabIndex={-1}
        aria-hidden={!onSubmit}
        aria-label={onSubmit ? "Search" : undefined}
        disabled={disabled}
        onClick={() => onSubmit?.(value.trim())}
        className={`absolute top-1/2 -translate-y-1/2 p-0 text-muted-foreground hover:bg-transparent ${
          onSubmit ? "hover:text-indigo-500" : "pointer-events-none"
        } ${sizing.icon}`}
      >
        <Search className="size-full" />
      </Button>

      <Input
        id={id}
        type="text"
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel ?? placeholder}
        placeholder={placeholder}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          /* Emptying the box drops the filter without needing Enter, matching
             the toolbar search elsewhere in the app. */
          if (next === "") onSubmit?.("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit?.(value.trim());
          if (e.key === "Escape" && value) clear();
        }}
        /* Input brings the border, ring and disabled treatment; only the
           room for the magnifier and the clear button is set here. */
        className={`border-border bg-background focus:border-indigo-500 ${sizing.field}`}
      />

      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={clear}
          aria-label="Clear search"
          className={`absolute top-1/2 -translate-y-1/2 rounded p-0 text-muted-foreground hover:bg-transparent hover:text-foreground ${sizing.clear}`}
        >
          <X className="size-full" />
        </Button>
      )}
    </div>
  );
}
